import React, { useEffect, useRef } from "react";

import { SeeInActionWrap, ORBIT_MS, orbitFrames } from "./seeInAction.style";
import { trackDemoCta } from "../../../common/analytics";
import { keepAnchorInView } from "../../../common/utils/keepAnchorInView";

const TARGET = "live_demo";

// With reduced motion the small light does not travel; it stands here, at
// the top right of the pill (a share of the way round from the top left).
const STILL_AT = 0.2;

// How the two glows outside the pill share the light, by how far the pointer
// is from the nearer end (in radii of the round end). Up to half a radius in,
// only the round glow shows, as at the right end. Further in, the pill hides
// more of it, so the glow along the top and bottom edges takes over: it comes
// up nearly to full before the round glow fades, so the light outside keeps
// its strength all the way, and it starts short, so near the end it does not
// reach out past it.
const SHARE_FROM = 0.5;
const SHARE_OVER = 0.65;
// share of the way: round glow, edge glow (opacity)
const SHARE = [
  [0, 1, 0],
  [0.4, 1, 0.75],
  [0.6, 0.75, 0.9],
  [1, 0, 1],
];
// share of the way: width of the edge glow
const EDGE_WIDTH = [
  [0, 0.35],
  [0.55, 0.35],
  [1, 0.8],
  [1.8, 0.9],
  [2.7, 1],
];
const along = (points, t) => {
  const i = points.findIndex((p) => p[0] >= t);
  if (i < 0) return points[points.length - 1].slice(1);
  if (i === 0) return points[0].slice(1);
  const [t0, ...a] = points[i - 1];
  const [t1, ...b] = points[i];
  const k = (t - t0) / (t1 - t0);
  return a.map((v, j) => v + (b[j] - v) * k);
};
// in the middle of the pill (keyboard focus, reduced motion) only the edge
// glow shows, at full width
const MIDDLE = { round: 0, edge: 1, width: 1, mid: 1 };
const shareAt = (e, r) => {
  const t = Math.max(0, (e / r - SHARE_FROM) / SHARE_OVER);
  const [round, edge] = along(SHARE, t);
  const [width] = along(EDGE_WIDTH, t);
  // 0 at the centres of the round ends or beyond, 1 a radius further in
  const mid = Math.min(1, Math.max(0, (e - r) / r));
  return { round, edge, width, mid };
};

// The hero's second button: it takes the visitor down to the interactive
// demo on this page (on phones the demo section shows its animated preview).
// It is a plain link, so it also works before the scripts load.
const SeeInAction = () => {
  const wrap = useRef(null);
  const link = useRef(null);

  // At rest a small light runs round the edge of the pill at an even speed,
  // one lap per ORBIT_MS. Its path depends on the size of the pill, so it is
  // worked out again whenever the pill changes size.
  useEffect(() => {
    const el = wrap.current;
    const a = link.current;
    if (!el || !a || !el.animate) return undefined;
    const dots = el.querySelectorAll(".sia_dot, .sia_dot_glow");
    const still = window.matchMedia("(prefers-reduced-motion: reduce)");
    let runs = [];
    const place = () => {
      if (!a.offsetWidth) return; // hidden: no path to follow
      const frames = orbitFrames(a.offsetWidth, a.offsetHeight);
      if (still.matches) {
        runs.forEach((run) => run.cancel());
        runs = [];
        const at = frames.find((f) => f.offset >= STILL_AT) || frames[0];
        dots.forEach((dot) => {
          dot.style.transform = at.transform;
        });
      } else if (runs.length) {
        runs.forEach((run) => run.effect.setKeyframes(frames));
      } else {
        dots.forEach((dot) => dot.style.removeProperty("transform"));
        // started in the same frame, the light on the edge and its glow
        // outside move as one
        runs = Array.from(dots, (dot) =>
          dot.animate(frames, { duration: ORBIT_MS, iterations: Infinity })
        );
      }
    };
    place();
    el.classList.add("has-orbit");
    const resized = window.ResizeObserver ? new ResizeObserver(place) : null;
    if (resized) resized.observe(a);
    if (still.addEventListener) still.addEventListener("change", place);
    return () => {
      if (resized) resized.disconnect();
      if (still.removeEventListener) still.removeEventListener("change", place);
      runs.forEach((run) => run.cancel());
      el.classList.remove("has-orbit");
    };
  }, []);

  // With a mouse the full light (glow around the pill, light inside, bright
  // edge) shows up where the pointer is and follows it along the pill, the
  // same at either end; it fades out where it is when the pointer leaves.
  // Keyboard focus lights the middle of the pill.
  const lightAt = (dx, share, cls) => {
    const el = wrap.current;
    el.style.setProperty("--glow-dx", `${dx}px`);
    el.style.setProperty("--glow-mid", share.mid.toFixed(2));
    el.style.setProperty("--glow-round", share.round.toFixed(2));
    el.style.setProperty("--glow-edge", share.edge.toFixed(2));
    el.style.setProperty("--glow-edge-w", share.width.toFixed(2));
    if (el.classList.contains("is-lit") || el.classList.contains("is-focus")) {
      el.classList.add(cls);
      return;
    }
    // it lights up in place instead of sliding in from where it was
    el.classList.add("is-jump", cls);
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => el.classList.remove("is-jump"))
    );
  };
  const follow = (event) => {
    const el = wrap.current;
    if (!el || event.pointerType !== "mouse") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      lightAt(0, MIDDLE, "is-lit"); // it stays in the middle
      return;
    }
    const box = el.getBoundingClientRect();
    const x = event.clientX - box.left;
    const share = shareAt(Math.min(x, box.width - x), box.height / 2);
    lightAt(Math.round(x - box.width / 2), share, "is-lit");
  };
  const rest = () => {
    const el = wrap.current;
    if (!el) return;
    el.classList.remove("is-lit");
    if (el.classList.contains("is-focus")) lightAt(0, MIDDLE, "is-focus");
  };
  const focus = (event) => {
    if (!wrap.current) return;
    let keyboard = true;
    try {
      keyboard = event.currentTarget.matches(":focus-visible");
    } catch (e) {
      // older browsers without :focus-visible light up on any focus
    }
    if (keyboard && !wrap.current.classList.contains("is-lit"))
      lightAt(0, MIDDLE, "is-focus");
  };
  const blur = () => {
    if (wrap.current) wrap.current.classList.remove("is-focus");
  };

  const go = (event) => {
    trackDemoCta("hero");
    // a new tab or window (Cmd/Ctrl/Shift-click) gets the plain link
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    const target = document.getElementById(TARGET);
    if (!target) return;
    event.preventDefault();
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // scrollIntoView keeps the section's scroll-margin-top under the navbar
    target.scrollIntoView({
      behavior: still ? "auto" : "smooth",
      block: "start",
    });
    try {
      window.history.replaceState(window.history.state, "", `#${TARGET}`);
    } catch (e) {
      // the address is cosmetic, the scroll already happened
    }
    keepAnchorInView(TARGET, { immediate: false });
  };

  return (
    <SeeInActionWrap ref={wrap} className="see_in_action">
      <span className="sia_halo" aria-hidden="true" />
      <span className="sia_halo_edge" aria-hidden="true" />
      <span className="sia_dot_glow" aria-hidden="true" />
      <a
        ref={link}
        href={`#${TARGET}`}
        onClick={go}
        onPointerMove={follow}
        onPointerLeave={rest}
        onFocus={focus}
        onBlur={blur}
      >
        <span className="sia_light" aria-hidden="true" />
        <span className="sia_rim" aria-hidden="true">
          <span className="sia_spark" />
          <span className="sia_dot" />
        </span>
        <span className="sia_label">See in action</span>
        <svg viewBox="0 0 20 10" aria-hidden="true" focusable="false">
          <path
            d="M1 5h17M13.5 1 18 5l-4.5 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </a>
    </SeeInActionWrap>
  );
};

export default SeeInAction;
